import { Check } from 'lucide-react'
import { Box, Typography } from '@mui/material'
import { useOnboarding } from '../context/OnboardingContext'
import { PROGRESS_PHASES, activePhaseId, clientLocation, entityLocation, phaseScore, type ProgressPhase } from './flow'
import { MISC_PRIMARY } from '../theme/onboardingTheme'

const TRACK_COLOR = '#3d6b7a'
const DONE_COLOR = '#059669'

/** Design manual: progress bar on every page with percentage, completed-section checkmarks, and current-section highlight. */
export function OnboardingPhaseProgress() {
  const { state, location, currentEntity, goTo, canReach, percentComplete } = useOnboarding()
  const activeId = activePhaseId(location)

  const targetFor = (phase: ProgressPhase) => {
    if (phase.section) {
      return currentEntity ? entityLocation(currentEntity.id, phase.section) : clientLocation('entityDashboard')
    }
    return clientLocation(phase.step ?? 'welcome')
  }

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flex: 1, height: 8, borderRadius: 999, bgcolor: '#e2e8f0', overflow: 'hidden' }} aria-hidden>
          <Box sx={{ height: '100%', width: `${percentComplete}%`, bgcolor: TRACK_COLOR, borderRadius: 999, transition: 'width 500ms ease-out' }} />
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a', whiteSpace: 'nowrap' }}>
          {percentComplete}% Complete
        </Typography>
      </Box>

      <Box
        role="list"
        aria-label="Onboarding sections"
        sx={{ mt: 2, display: 'flex', gap: 0.5, overflowX: 'auto', pb: 0.5 }}
      >
        {PROGRESS_PHASES.map((phase, i) => {
          const isActive = phase.id === activeId
          const done = phaseScore(state, phase) >= 1
          const inEntityLoop = Boolean(phase.section)
          const target = targetFor(phase)
          const reachable = isActive || canReach(target)
          return (
            <Box key={phase.id} role="listitem" sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
              <Box
                component="button"
                type="button"
                onClick={() => goTo(target)}
                disabled={!reachable}
                title={reachable ? undefined : 'Complete the earlier steps first'}
                aria-current={isActive ? 'step' : undefined}
                aria-label={`${phase.label}${done ? ' (complete)' : ''}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  cursor: 'pointer',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: isActive ? MISC_PRIMARY : inEntityLoop ? '#cbd5e1' : 'transparent',
                  borderStyle: inEntityLoop && !isActive ? 'dashed' : 'solid',
                  bgcolor: isActive ? '#eff6ff' : 'transparent',
                  px: 1.25,
                  py: 0.5,
                  fontSize: 12,
                  fontWeight: 600,
                  color: isActive ? MISC_PRIMARY : done ? '#065f46' : '#475569',
                  '&:hover': { bgcolor: '#f8fafc', color: MISC_PRIMARY },
                  '&:disabled': { cursor: 'not-allowed', opacity: 0.5, bgcolor: 'transparent', color: '#475569' },
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    display: 'grid',
                    placeItems: 'center',
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    bgcolor: done ? DONE_COLOR : isActive ? MISC_PRIMARY : '#cbd5e1',
                    color: '#ffffff',
                  }}
                >
                  {done ? <Check size={11} strokeWidth={3} /> : null}
                </Box>
                {phase.label}
              </Box>
              {i < PROGRESS_PHASES.length - 1 ? (
                <Box aria-hidden sx={{ color: '#cbd5e1', fontSize: 12, px: 0.25 }}>
                  ›
                </Box>
              ) : null}
            </Box>
          )
        })}
      </Box>
      <Typography sx={{ mt: 0.5, fontSize: 11, color: '#94a3b8' }}>
        Dashed sections repeat for every business entity.
      </Typography>
    </Box>
  )
}
